import React from 'react';
import {
  MarketingLayout,
  Section,
  Hero,
  FeatureGrid,
  CallToAction,
} from '@byscott-io/corebyscott';

const features = [
  {
    icon: '💳',
    title: 'Square Integration',
    description: 'Seamlessly connect with Square for payment processing and customer management without complex setup.',
  },
  {
    icon: '📊',
    title: 'Multi-App Billing',
    description: 'Manage subscriptions, pricing tiers, and entitlements across all your SaaS applications from one dashboard.',
  },
  {
    icon: '🎯',
    title: 'Flexible Pricing Models',
    description: 'Support flat-rate, per-user, usage-based, and tiered pricing models to match any business need.',
  },
  {
    icon: '🔐',
    title: 'Feature Entitlements',
    description: 'Control access to features with powerful entitlement checks. Enable or disable features per subscription level.',
  },
  {
    icon: '📈',
    title: 'Usage Tracking',
    description: 'Track and meter usage for quota-based billing. Set limits and monitor consumption in real-time.',
  },
  {
    icon: '🎁',
    title: 'Coupons & Trials',
    description: 'Offer promotional discounts and free trials to attract new customers and boost conversions.',
  },
];

export default function LandingPage() {
  return (
    <MarketingLayout>
      <Hero
        headline="Billing Made Simple"
        subheadline="The complete subscription management platform for SaaS applications. Manage pricing, entitlements, and payments across all your apps."
        primaryCta={{ label: 'Get Started', href: '/register' }}
        secondaryCta={{ label: 'Learn More', href: '/features' }}
      />

      <Section background="gray">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900">Everything You Need</h2>
          <p className="mt-4 text-lg text-gray-600">
            Powerful features to manage billing across your entire product portfolio
          </p>
        </div>
        <FeatureGrid features={features} columns={3} />
      </Section>

      <Section>
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900">How It Works</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="text-center">
            <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl font-bold text-indigo-600">1</span>
            </div>
            <h3 className="text-xl font-semibold mb-2">Register Your Apps</h3>
            <p className="text-gray-600">
              Add your SaaS applications and configure their unique API keys for secure communication.
            </p>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl font-bold text-indigo-600">2</span>
            </div>
            <h3 className="text-xl font-semibold mb-2">Define Products & Pricing</h3>
            <p className="text-gray-600">
              Create products with multiple pricing levels. Set up features, quotas, and settings for each tier.
            </p>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl font-bold text-indigo-600">3</span>
            </div>
            <h3 className="text-xl font-semibold mb-2">Integrate & Bill</h3>
            <p className="text-gray-600">
              Use our client SDK to check entitlements and manage subscriptions. We handle the rest.
            </p>
          </div>
        </div>
      </Section>

      <CallToAction
        headline="Ready to simplify your billing?"
        subheadline="Join the growing number of SaaS businesses using BillingByScott to manage their subscriptions."
        primaryCta={{ label: 'Start Free Trial', href: '/register' }}
        secondaryCta={{ label: 'Contact Us', href: '/contact' }}
        background="indigo"
      />
    </MarketingLayout>
  );
}
